<template>
  <div>
    <aside class="sidebar"
           :class="{ collapsed: collapsed, hasBitrix: hasBitrix }">
      <router-link :to="{ name: 'Calendar' }"
                   class="d-block sidebar__logo-wrapper">
        <img :src="`/images/general/${!isDentalClinic ? 'logo_medical_short.png' : 'logo_short.png'}`"
             class="sidebar__logo sidebar__logo-short"/>
        <img :src="`/images/general/${!isDentalClinic ? 'logo_medical.png' : 'logo.png'}`"
             class="sidebar__logo sidebar__logo-full"/>
      </router-link>
      <MenuToggler view="sidebar"/>
      <div class="sidebar__menu">
        <template v-for="(m, mind) in topmenu">
          <div v-if="isMenuItemVisible(m)"
               @mouseenter="mouseOver(m.title, $event)"
               @mouseleave="mouseLeave"
               :key="`topmenu-${mind}`"
               class="side-menu-item">
            <a v-if="m.rightPopup && !rights.includes(m.rightPopup) && hasFreeTariff"
               class="sidebar__item"
               @click="$bvModal.show('module-not-in-tariff-modal')">
              <component :is="m.i"
                         :class="`sidebar__svg-${m.title}`"/>
              <span class="sidebar__title">{{ $t(m.title) }}</span>
            </a>
            <router-link v-else-if="!m.rightPopup || rights.includes(m.rightPopup)"
                         class="sidebar__item"
                         :class="m.class"
                         :to="m.to">
              <component :is="m.i" :class="`sidebar__svg-${m.title}`"/>
              <span class="sidebar__title">{{ $t(m.title) }}</span>
            </router-link>
          </div>
        </template>
        <div class="sidebar__separator" v-if="hasSettingsPart">
          <span>{{ $t('control') }}</span>
        </div>
        <template v-for="(m, mind) in bottommenu">
          <div v-if="isMenuItemVisible(m)"
               @mouseenter="mouseOver(m.title, $event)"
               @mouseleave="mouseLeave"
               :key="`bottommenu-${mind}`" >
            <a v-if="m.rightPopup && !rights.includes(m.rightPopup) && hasFreeTariff"
               class="sidebar__item"
               @click="$bvModal.show('module-not-in-tariff-modal')">
              <component :is="m.i"/>
              <span class="sidebar__title">{{ $t(m.title) }}</span>
            </a>
            <router-link v-else-if="!m.rightPopup || rights.includes(m.rightPopup)"
                         class="sidebar__item"
                         :to="m.to">
              <component :is="m.i"/>
              <span class="sidebar__title" v-html="$t(m.title)"/>
            </router-link>
          </div>
        </template>
      </div>
    </aside>
    <span class="sidebar-tooltip"
          v-if="tooltipTitle && collapsed && !isMobile"
          :style="{ top: `${tooltipTop}px` }">
      {{ tooltipTitle }}
    </span>
  </div>
</template>

<script setup>
import BoxSvg from '@/assets/svg-vue/sidebar/box.svg'
import CalculatorSvg from '@/assets/svg-vue/sidebar/calculator.svg'
import CalendarSvg from '@/assets/svg-vue/sidebar/calendar.svg'
import CashSvg from '@/assets/svg-vue/sidebar/cash.svg'
import DoctorSvg from '@/assets/svg-vue/sidebar/doctor.svg'
import FolderSvg from '@/assets/svg-vue/sidebar/folder.svg'
import PaperSvg from '@/assets/svg-vue/sidebar/paper.svg'
import PatientsSvg from '@/assets/svg-vue/sidebar/patients.svg'
import SettingsSvg from '@/assets/svg-vue/sidebar/settings.svg'
import TemplatesSvg from '@/assets/svg-vue/sidebar/template.svg'
import CashdeskSvg from '@/assets/svg-vue/sidebar/cashdesk.svg'
import EgiszSvg from '@/assets/svg-vue/patients/egisz.svg'
import ProcessSvg from '@/assets/svg-vue/sidebar/process.svg'
import LogSvg from '@/assets/svg-vue/sidebar/log.svg'
import ShieldSvg from '@/assets/svg-vue/sidebar/shield.svg'
import CRMSvg from '@/assets/svg-vue/sidebar/crm.svg'
import BillingSvg from '@/assets/svg-vue/sidebar/billing.svg'
import MarketingSvg from '@/assets/svg-vue/sidebar/marketing.svg'
import IncomeSvg from '@/assets/svg-vue/sidebar/dollar.svg'
import PresentSvg from '@/assets/svg-vue/sidebar/gift.svg'
import LabSvg from '@/assets/svg-vue/sidebar/lab.svg'
import MarketSvg from '@/assets/svg-vue/sidebar/market.svg'
import NoteSvg from '@/assets/svg-vue/sidebar/note.svg'
import DiagnocatSvg from '@/assets/svg-vue/vendor/diagnocat.svg'
import TreatmentSvg from '@/assets/svg-vue/services/treatment.svg'
import { useStore } from "vuex"
import MenuToggler from "@/components/parts/header/MenuToggler"
import { computed, shallowRef, ref, onBeforeMount, getCurrentInstance, onMounted } from "vue"

const topmenu = ref([
  { i: shallowRef(CalendarSvg), to: '/calendar', title: 'calendar' },
  { i: shallowRef(CashSvg), to: '/cash-register', title: 'cashdesk', right: 'cashregister' },
  { i: shallowRef(PatientsSvg), to: '/patients', title: 'patients', right: 'patients' },
  { i: shallowRef(TreatmentSvg), to: '/visits', title: 'visits', right: 'visit_history_section', class: 'stroked' },
  { i: shallowRef(ProcessSvg), to: '/treatment_plans', title: 'plan_treatments', right: 'planning_list' },
  { i: shallowRef(DoctorSvg), to: '/staff', title: 'staff', right: 'staff' },
  { i: shallowRef(FolderSvg), to: '/services', title: 'services', right: 'clinic_services' },
  { i: shallowRef(BoxSvg), to: '/warehouses', title: 'warehouse', rightPopup: 'materials' },
  { i: shallowRef(LabSvg), to: '/laboratories', title: 'laboratories', right: 'laboratories' },
  { i: shallowRef(PaperSvg), to: '/reports', title: 'reports', rightPopup: 'reports_tab' },
  { i: shallowRef(CalculatorSvg), to: '/accounting', title: 'accounting', rightPopup: 'accounting' },
  { i: shallowRef(CalculatorSvg), to: '/expenses', title: 'expenses', rightPopup: 'enter_expenses', rightNot: 'accounting' },
  { i: shallowRef(IncomeSvg), to: '/income', title: 'income', rightPopup: 'enter_income', rightNot: 'accounting' },
  { i: shallowRef(MarketingSvg), to: '/marketing', title: 'marketing', right: 'marketing', countries: ['RU'] },
  { i: shallowRef(CRMSvg), to: '/crm', title: 'crm', right: 'crm' },
  { i: shallowRef(EgiszSvg), to: '/egisz/kanban', title: 'egisz', right: 'egisz_view', class: 'stroked', countries: ['RU'] },
  { i: shallowRef(DiagnocatSvg), to: '/diagnocat', title: 'Diagnocat', right: 'diagnocat', class: 'stroked', countries: ['RU'] },
  { i: shallowRef(PresentSvg), to: '/gift', title: 'get_gift', class: 'stroked' },
  { i: shallowRef(MarketSvg), to: '/market', title: 'market', countries: ['RU'] }
])

const bottommenu = ref([
  { i: shallowRef(SettingsSvg), to: '/settings', title: 'settings', right: 'settings' },
  { i: shallowRef(BillingSvg), to: '/billing', title: 'billing', right: 'billing' },
  { i: shallowRef(TemplatesSvg), to: '/templates', title: 'templates', right: 'treatment_templates' },
  { i: shallowRef(CashdeskSvg), to: '/cashdesk', title: 'fiscal_cashdesk', right: 'cashdesk', countries: ['RU'] },
  { i: shallowRef(ShieldSvg), to: '/insurance', title: 'insurance', right: 'insurance' },
  { i: shallowRef(LogSvg), to: '/logs', title: 'logs_journal', right: 'log_history' },
  // { i: shallowRef(LabSvg), to: '/laboratories', title: 'dental_work', right: 'laboratories' },
])

const tooltipTop = ref(0)
const tooltipTitle = ref(null)

const store = useStore()
let i18n = null

const collapsed = computed(() => store.state.dom.sidebarCollapsed)
const rights = computed(() => store.state.auth.rights)
const serverCountry = computed(() => store.state.auth.serverCountry)
const legalDocumentsEnabled = computed(() => store.state.auth.legalDocumentsEnabled)
const user = computed(() => store.state.auth.user)
const clinic = computed(() => store.state.auth.clinic)
const isDentalClinic = computed(() => store.getters.isDentalClinic)
const userId = computed(() => user.value?.id || null)
const isMobile = computed(() => store.state.dom.isMobile)
const isMenuItemVisible = (m) => {
  if(m.right && !rights.value.includes(m.right)) return false
  if(m.rightNot && rights.value.includes(m.rightNot)) return false
  if(m.countries && !m.countries.includes(serverCountry.value)) return false
  return true
}

const hasSettingsPart = computed(() => bottommenu.value.some(isMenuItemVisible))
const hasBitrix = computed(() => {
  return clinic.value?.support_button === "1"
})
const hasFreeTariff = computed(() => store.getters.hasFreeTariff)

const mouseOver = (title, event) => {
  const rect = event.target?.getBoundingClientRect()
  if(!rect) return
  tooltipTitle.value = i18n.t(title)
  tooltipTop.value = rect.top + rect.height / 2 - 12
}

const mouseLeave = () => {
  tooltipTitle.value = null
}

onBeforeMount(() => {
  i18n = (getCurrentInstance())?.ctx?.$i18n
})

onMounted(() => {
  if(user.value?.role_id == 5) {
    topmenu.value.push({
      i: PatientsSvg,
      to: '/patients/'+userId.value,
      title: 'card',
    })
  }
  if(legalDocumentsEnabled.value) {
    bottommenu.value.push({ i: shallowRef(NoteSvg), to: '/legal-documents', title: 'documents' })
  }
  // if(clinic.value?.id === 1843) {
  //   topmenu.value.push(
  //       { i: shallowRef(CashSvg), to: '/cash-register', title: 'cashdesk', right: 'cashdesk1' },
  //   )
  // }
})
</script>

<style scoped lang="scss">
.sidebar__svg-report {
  margin-left: 1px;
}
.sidebar-tooltip {
  position: fixed;
  left: 97px;
  z-index: 20;
  background: #000;
  color: #fff;
  border-radius: 4px;
  height: 24px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  font-size: 13px;
}
</style>
